import {
  Box, Checkbox, Flex, FormControl,
  FormErrorMessage,
  FormLabel,
  HStack, IconButton, Input,
  InputGroup,
  InputRightElement, Link, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Text
} from "@chakra-ui/react"
import type { FieldProps } from "formik"
import { Field } from "formik"
import moment from "moment"
import React, { useCallback, useContext, useMemo, useState } from "react"
import DatePicker from "react-datepicker"
import { Trans, useTranslation } from "react-i18next"
import { BsInfoCircleFill } from "react-icons/bs"
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5"
import { Req } from "use-http"
import * as Yup from "yup"
import { colors } from "../../../../ui-kit"
import { FeaturesEnum } from "../../../../utils/features"
import useToast from "../../../hooks/useToast"
import { PublicLinkResponse, PublicLinksResponse } from "../../../interfaces"
import { PublicLinksForAClientResponse } from "../../../interfaces/PublicLinks"
import { UserContext } from "../../../providers"
import DateCustomInput from "../../DateCustomInput"
import { InfoPopover } from "../../ExternalStorage/InfoPopover"

interface FormProps {
  assetType: string
  validFrom: string | null,
  validTill: string | null,
  password?: string,
  exposeDraftAssets?: boolean,
}

const usePublicLinkForm = ({
  request,
}: {
  request: Req<PublicLinksResponse | PublicLinksForAClientResponse>
}): {
  formFieldComponents: React.ReactElement
  getFormikInitialValues: (args: {
    publicLink: PublicLinkResponse | undefined
    assetType: string
  }) => FormProps
  formValidationSchema: Yup.ObjectSchema<any>
  getPublicLinks: (args: {
    url: string
    sideEffects: (response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => void
  }) => Promise<void>
  createOrUpdatePublicLink: (args: {
    formValues: FormProps,
    publicLink: PublicLinkResponse | undefined
    assetId: string
    getUrl: string
    sideEffects: (response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => void
  }) => Promise<void>
  deletePublicLink: (args: {
    publicLink: PublicLinkResponse
    getUrl: string
    sideEffects: (response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => void
  }) => Promise<void>
} => {
  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const toast = useToast()
  const [showPasswordFlag, setShowPasswordFlag] = useState<boolean>(false)

  const getPublicLinks = useCallback(async ({
    url,
    sideEffects,
  }: {
    url: string
    sideEffects: (response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => void
  }) => {
    const response = await request.get(url)
    if (request.error) {
      toast({
        description: t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      sideEffects(undefined)
    } else {
      sideEffects(response)
    }
  }, [user?.clientNumber, request])

  const createOrUpdatePublicLink = useCallback(async ({
    formValues,
    publicLink,
    assetId,
    getUrl,
    sideEffects,
  }: {
    formValues: FormProps
    publicLink: PublicLinkResponse | undefined
    assetId: string
    getUrl: string
    sideEffects: (response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => void
  }) => {
      const reqBody = {
        validFrom: formValues.validFrom ?? undefined,
        validTill: formValues.validTill ?? undefined,
        password: formValues.password?.length ? formValues.password : undefined,
        exposeDraftAssets: formValues.exposeDraftAssets ?? undefined,
      }
      let localResponse
      if (!publicLink) {
        localResponse = await request.post(
          `/api/media-library/${user?.clientNumber}/assets/${assetId}/public-links/`,
          {
            ...reqBody,
          }
        )
      } else {
        localResponse = await request.put(
          `/api/media-library/${user?.clientNumber}/assets/${publicLink.assetInfo.id}/public-links/${publicLink._id}`,
          {
            ...reqBody,
          }
        )
      }
      if (request.error) {
        toast({
          description: localResponse?.message ?? t(
            "something_went_wrong",
            "Something went wrong. Please try again."
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        toast({
          description: t(
            `public_link_${publicLink ? "updated" : "created"}_successfully`,
            `Public link ${publicLink ? "updated" : "created"} successfully`,
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        void getPublicLinks({
          url: getUrl,
          sideEffects,
        })
      }
  }, [user?.clientNumber, getPublicLinks, request])

  const deletePublicLink = useCallback(async ({
    publicLink,
    getUrl,
    sideEffects,
  }: {
    publicLink: PublicLinkResponse
    getUrl: string
    sideEffects: (response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => void
  }) => {
    if (publicLink) {
      await request.delete(`/api/media-library/${user?.clientNumber}/assets/${publicLink.assetInfo.id}/public-links/${publicLink?._id}`)
    }
    if (request.error) {
      toast({
        description: t(
          "something_went_wrong",
          "Something went wrong. Please try again."
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      toast({
        description: t(
          `public_link_deleted_successfully`,
          `Public link deleted successfully`,
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      void getPublicLinks({
        url: getUrl,
        sideEffects,
      })
    }
  }, [user?.clientNumber, request, getPublicLinks])

  const formFieldComponents = useMemo(() => {
    return (
      <>
        <Field name="password">
          {({ field, form }: FieldProps<string, FormProps>) => (
            <FormControl
              isInvalid={Boolean(
                form.errors.password && form.touched.password,
              )}
            >
              <FormLabel>
                <HStack>
                  <Text>
                    {t('password_uppercase_p', 'Password')}
                  </Text>
                  <InfoPopover
                    placement="bottom-start"
                    info={"public_links_form_password_description"}
                  />
                </HStack>
              </FormLabel>
              <InputGroup>
                <Input
                  {...field}

                  placeholder="Enter password (optional)"
                  background="white"
                  type={showPasswordFlag ? "text" : "password"}
                  autoComplete="new-password"  // Disable autofill
                />
                <InputRightElement width="4.5rem">
                  {showPasswordFlag ? (
                    <IoEyeOutline onClick={() => {setShowPasswordFlag(false)}} />
                  ) : (
                    <IoEyeOffOutline onClick={() => {setShowPasswordFlag(true)}} />
                  )}
                </InputRightElement>
              </InputGroup>
              {form.errors.password && form.touched.password ? (
                  <FormErrorMessage mt="0.25em">
                    {form.errors.password}
                  </FormErrorMessage>
                ) : (
                  <Box height="1.55em" />
                )}
            </FormControl>
          )}
        </Field>
        <HStack>
          <Field name="validFrom">
            {({ field, form }: FieldProps<string, FormProps>) => (
              <FormControl
                isInvalid={Boolean(
                  form.errors.validFrom && form.touched.validFrom,
                )}
              >
                <FormLabel>
                  {t('valid_from', 'Valid from')}
                </FormLabel>
                <DatePicker
                  isClearable
                  placeholderText="Pick a date (optional)"
                  minDate={
                    moment(
                      Math.min(
                        Date.now(),
                        form.values.validFrom ? moment(form.values.validFrom).valueOf() : Date.now()
                      )
                    )
                    .startOf("day")
                    .toDate()
                  }
                  maxDate={form.values.validTill ? moment(new Date(form.values.validTill)).startOf("day").toDate() : undefined}
                  customInput={React.createElement(React.forwardRef(DateCustomInput),  { placeholder: "Pick a date (optional)" })}
                  name={field.name}
                  selected={field.value ? moment(new Date(field.value)).startOf("day").toDate() : null}
                  onChange={(newDate) => {form.setFieldValue(field.name, newDate ? moment(newDate).startOf("day").toISOString() : null)}}
                />
                {form.errors.validFrom && form.touched.validFrom ? (
                  <FormErrorMessage mt="0.25em">
                    {form.errors.validFrom}
                  </FormErrorMessage>
                ) : (
                  <Box height="1.55em" />
                )}
              </FormControl>
            )}
          </Field>
          <Field name="validTill">
            {({ field, form }: FieldProps<string, FormProps>) => (
              <FormControl
                isInvalid={Boolean(
                  form.errors.validTill && form.touched.validTill,
                )}
              >
                <FormLabel>
                  {t('valid_till', 'Valid till')}
                </FormLabel>
                <DatePicker
                  isClearable
                  placeholderText="Pick a date (optional)"
                  minDate={
                    form.values.validFrom ?
                    moment(new Date(form.values.validFrom)).startOf("day").toDate() :
                    moment(
                      Math.min(
                        Date.now(),
                        form.values.validTill ? moment(form.values.validTill).valueOf() : Date.now()
                      )
                    )
                    .startOf("day")
                    .toDate()
                  }
                  customInput={React.createElement(React.forwardRef(DateCustomInput),  { placeholder: "Pick a date (optional)" })}
                  name={field.name}
                  selected={field.value ? moment(new Date(field.value)).startOf("day").toDate() : null}
                  onChange={(newDate) => {form.setFieldValue(field.name, newDate ? moment(newDate).endOf("day").toISOString() : null)}}
                />
                {form.errors.validTill && form.touched.validTill ? (
                  <FormErrorMessage mt="0.25em">
                    {form.errors.validTill}
                  </FormErrorMessage>
                ) : (
                  <Box height="1.55em" />
                )}
              </FormControl>
            )}
          </Field>
        </HStack>
        <Field name="exposeDraftAssets">
          {({ field, form }: FieldProps<string, FormProps>) => (
            form.values.assetType !== "file" ? (
              <FormControl
                isInvalid={Boolean(
                  form.errors.exposeDraftAssets && form.touched.exposeDraftAssets,
                )}
              >
                <FormLabel>
                  <HStack spacing={0}>
                    <Checkbox
                      id={field.name}
                      name={field.name}
                      isChecked={form.values["exposeDraftAssets"]}
                      onChange={field.onChange}
                      isDisabled={
                        !user?.pricingPlan.featuresList.includes(
                          FeaturesEnum.DRAFT_ASSETS,
                        ) ?? true
                      }
                    >
                      <Text>
                        {t('expose_unpublished_assets', 'Expose unpublished assets')}
                      </Text>
                    </Checkbox>
                    <Popover trigger="hover" placement="right">
                      <PopoverTrigger>
                        <Flex mb={2}>
                          <IconButton
                            aria-label="Info icon"
                            icon={<BsInfoCircleFill />}
                            color={colors.appBlueyGrey}
                            size="sm"
                            variant="link"
                          />
                        </Flex>
                      </PopoverTrigger>
                      <PopoverContent width="md">
                        <PopoverArrow />
                        <PopoverBody>
                          <Text>
                            {user?.pricingPlan.featuresList.includes(
                              FeaturesEnum.DRAFT_ASSETS,
                            ) ? (
                              t('expose_unpublished_assets_description')
                            ) : (
                              <Trans t={t} i18nKey="draft_asset_plan_info">
                                <Text>
                                  Draft assets feature is not available in your
                                  current plan.{' '}
                                  <Link
                                    isExternal
                                    lineHeight={1.2}
                                    href="https://docs.imagekit.io/features/user-access-management#media-library-restricted-access"
                                  >
                                    {t('learn_more', 'Learn more')}
                                  </Link>
                                  .
                                  <br />
                                  <Link
                                    isExternal
                                    href="https://imagekit.io/contact-us/"
                                  >
                                    Contact sales
                                  </Link>{' '}
                                  to enable this feature.
                                </Text>
                              </Trans>
                            )}
                          </Text>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </HStack>
                </FormLabel>

                {form.errors.password && form.touched.password ? (
                    <FormErrorMessage mt="0.25em">
                      {form.errors.password}
                    </FormErrorMessage>
                  ) : (
                    <Box height="1.55em" />
                  )}
              </FormControl>
            ) : null
          )}
        </Field>
      </>
    )
  }, [
    showPasswordFlag,
    setShowPasswordFlag,
  ])

  const getFormikInitialValues = useCallback(({
    publicLink,
    assetType,
  }: {
    publicLink: PublicLinkResponse | undefined
    assetType: string
  }) => {
    return {
      assetType,
      validFrom: publicLink?.validFrom ?? null,
      validTill: publicLink?.validTill ?? null,
      password: publicLink?.password ?? "",
      exposeDraftAssets: publicLink?.exposeDraftAssets,
    }
  }, [])

  const formValidationSchema = useMemo(
    () => Yup.object().shape({
      validFrom: Yup.date().nullable(),
      validTill: Yup.date().nullable(),
      password: Yup.string().max(100, "Password cannnot be longer than 100 characters"),
    }),
    [],
  )

  return {
    formFieldComponents,
    getFormikInitialValues,
    formValidationSchema,
    getPublicLinks,
    createOrUpdatePublicLink,
    deletePublicLink,
  }
}

export default usePublicLinkForm
